import type { CSSProperties } from 'react';
import { resolveColorHex, type ColorHexMaps, type ColorSlot } from './colorHex';

export const COLOR_SLOTS = ['frame', 'upholstery', 'stitches', 'arm'] as const satisfies readonly ColorSlot[];

export const COLOR_SLOT_LABELS: Record<ColorSlot, string> = {
    frame: 'Estrutura',
    upholstery: 'Estofado',
    stitches: 'Costura',
    arm: 'Apoio de braço',
};

export const NO_HEX_PATTERN = 'repeating-linear-gradient(45deg,#d1d5db 0 3px,transparent 3px 6px)';

export const isNoCustomization = (name: string | null | undefined) =>
    !name || ['', 'N/A', 'NA', '-'].includes(name.trim().toUpperCase());

/** standard = cor do cadastro (com hex) · none = sem customização · off = nome fora do padrão/de-para. */
export type DotState = 'standard' | 'none' | 'off';

export function dotState(
    slot: ColorSlot,
    name: string | null | undefined,
    hexMaps: Partial<ColorHexMaps> | null | undefined,
): { state: DotState; hex: string | null } {
    if (isNoCustomization(name)) return { state: 'none', hex: null };
    const hex = resolveColorHex(slot, name, hexMaps);
    return hex ? { state: 'standard', hex } : { state: 'off', hex: null };
}

export const DOT_STYLE: Record<DotState, string> = {
    standard: 'shadow-sm ring-1 ring-black/10',
    // ⊘ opaco: o vazado "mordia" a bolinha vizinha nas pilhas sobrepostas (card do deal).
    none: 'ring-1 ring-gray-300',
    off: 'ring-2 ring-amber-400',
};

export const NO_COLOR_SLASH =
    'linear-gradient(135deg, transparent calc(50% - 1px), #9ca3af calc(50% - 1px), #9ca3af calc(50% + 1px), transparent calc(50% + 1px))';

export function dotInlineStyle(state: DotState, hex: string | null): CSSProperties | undefined {
    if (state === 'standard') return { backgroundColor: hex ?? undefined };
    if (state === 'off') return { backgroundColor: '#fef3c7', backgroundImage: NO_HEX_PATTERN };
    return { backgroundColor: '#f3f4f6', backgroundImage: NO_COLOR_SLASH };
}

export function colorDotLabel(slot: ColorSlot, name: string | null | undefined, state: DotState): string {
    if (state === 'none') return `${COLOR_SLOT_LABELS[slot]}: sem customização`;
    if (state === 'off') return `${COLOR_SLOT_LABELS[slot]}: ${name} (fora do padrão)`;
    return `${COLOR_SLOT_LABELS[slot]}: ${name}`;
}

const SIZE = { sm: 'size-4', md: 'size-5', lg: 'size-10' } as const;

export function ColorDot({
    slot,
    name,
    hexMaps,
    size = 'sm',
    title,
}: {
    slot: ColorSlot;
    name: string | null | undefined;
    hexMaps: Partial<ColorHexMaps> | null | undefined;
    size?: keyof typeof SIZE;
    title?: string;
}) {
    const { state, hex } = dotState(slot, name, hexMaps);
    const label = title ?? colorDotLabel(slot, name, state);

    return (
        <span
            title={label}
            aria-label={label}
            role="img"
            className={`inline-flex shrink-0 rounded-full transition-transform duration-150 hover:scale-125 ${SIZE[size]} ${DOT_STYLE[state]}`}
            style={dotInlineStyle(state, hex)}
        />
    );
}

export function ColorLegend({ prefix }: { prefix?: string }) {
    return (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500">
            {prefix && <span className="font-medium text-gray-600">{prefix}</span>}
            <span className="inline-flex items-center gap-1">
                <span className={'inline-flex size-3 rounded-full bg-gray-700 ' + DOT_STYLE.standard} /> padrão
            </span>
            <span className="inline-flex items-center gap-1">
                <span className={'inline-flex size-3 rounded-full ' + DOT_STYLE.none} style={dotInlineStyle('none', null)} />{' '}
                sem customização
            </span>
            <span className="inline-flex items-center gap-1">
                <span className={'inline-flex size-3 rounded-full ' + DOT_STYLE.off} style={dotInlineStyle('off', null)} />{' '}
                fora do padrão
            </span>
        </div>
    );
}
